import { Root, Trigger } from '@radix-ui/react-tooltip';
import { useCallback, useEffect, useRef, useState } from 'react';

import useOnClickOutside from '../../hooks/useOnClickOutside';
import { OmitStrict } from '../../types';

import PortalTooltipContent from './PortalTooltipContent';
import { TooltipProps } from './types';

type Props = React.PropsWithChildren<
  OmitStrict<TooltipProps, 'isClickMode' | 'isDisabled'>
>;

/**
 * Tooltip that is opened on click
 */
const ClickModeTooltip: React.FC<Props> = ({
  children,
  content,
  asChild = true,
  defaultOpen,
  onOpenChange,
  ...contentProps
}) => {
  const [isOpenByClick, setOpenByClick] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(contentRef, () => {
    setOpenByClick(false);
  });

  const handleKeyDown = useCallback(
    (
      event: KeyboardEvent | React.KeyboardEvent,
      isDefaultPrevented?: boolean,
    ) => {
      if (['Escape', 'Enter', ' '].includes(event.key) && isOpenByClick) {
        if (isDefaultPrevented) event.preventDefault();
        setOpenByClick(false);
      }
    },
    [isOpenByClick],
  );

  useEffect(() => {
    document.body?.addEventListener('keydown', handleKeyDown);

    () => {
      document.body?.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Root
      defaultOpen={defaultOpen}
      open={isOpenByClick}
      onOpenChange={onOpenChange}>
      <Trigger
        asChild={asChild}
        onClick={() => setOpenByClick(prevValue => !prevValue)}
        onKeyDown={event => handleKeyDown(event, true)}>
        {children}
      </Trigger>
      <PortalTooltipContent
        content={content}
        ref={contentRef}
        {...contentProps}>
        {children}
      </PortalTooltipContent>
    </Root>
  );
};

export default ClickModeTooltip;
